import React, { useCallback } from "react";
import { Box, Button } from "@mui/material";
import { ChatAction } from "../models/chatBotModels";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { sendMessage, markActionClicked, attachActionRequest } from "../chatBotSlice";
import { mapActionIdToType } from "../actions/actionRegistry";
import { CREATE_SERVICE_REPORT_SCHEMA } from "../actions/actionDefinitions";
import useTranslation from "../../hooks/useTranslation";
import { dt } from "../../themes/redesignTokens";

// ===================================================================
//  MIDAS Design Tokens (local subset)
// ===================================================================

const midas = {
  gold: "#D4A017",
  goldDark: "#A67C00",
  goldSoft: "rgba(212, 160, 23, 0.08)",
};

// ===================================================================
//  MA-0a: Hardcoded schema map for intercepted actions.
//  Until the dynamic schema endpoint exists, we use these.
// ===================================================================

const HARDCODED_SCHEMAS: Record<string, typeof CREATE_SERVICE_REPORT_SCHEMA> = {
  "machinery.create_service_report": CREATE_SERVICE_REPORT_SCHEMA,
};

// ===================================================================
//  ChatActionButtons — renders action buttons below a bot message.
//  MA-0a: Intercepts known action IDs (e.g., create_sr) and triggers
//  the action framework instead of sending back to bot.
//  Unknown action IDs fall back to old behavior (backward compat).
// ===================================================================

interface ChatActionButtonsProps {
  messageId: string;
  actions: ChatAction[];
}

const ChatActionButtons: React.FC<ChatActionButtonsProps> = ({
  messageId,
  actions,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const activeThreadId = useAppSelector((s) => s.midasPage?.activeThreadId);
  // MIDAS-SCOPESTREAM: Read isLoading from the active thread's buffer
  const threadBuffer = useAppSelector((s) => {
    const tid = s.midasPage?.activeThreadId || "popup";
    return s.chatBot.threadBuffers[tid];
  });
  const isLoading = threadBuffer?.isLoading || false;

  // Find if an action was already clicked for this message
  const message = useAppSelector((s) =>
    s.chatBot.messages.find((m) => m.id === messageId)
  );
  const clickedActionId = message?.clickedActionId;
  const isActedOn = !!clickedActionId;

  // Check if this message already has an actionRequest attached (skip rendering buttons)
  const hasActionRequest = !!message?.actionRequest;

  const handleClick = useCallback(
    (action: ChatAction) => {
      if (isActedOn || isLoading) return;

      // MA-0a: Check if this action ID maps to a registered action type
      const mappedType = mapActionIdToType(action.id);

      if (mappedType) {
        // Intercepted action — trigger the action framework locally
        const schema = HARDCODED_SCHEMAS[mappedType];
        if (schema) {
          // Mark action group as acted on
          dispatch(markActionClicked({ messageId, actionId: action.id }));

          // Build action request from action.context + hardcoded schema
          const context = (action.context || {}) as Record<string, any>;
          const actionId = `${action.id}-${Date.now()}`;

          dispatch(
            attachActionRequest({
              messageId,
              actionRequest: {
                action_id: actionId,
                action_type: mappedType,
                schema: {
                  fields: schema.fields,
                },
                values: {
                  machine_id: context.machine_id || machineContext?.machineId || "",
                  machine_name: context.machine_name || machineContext?.model || "",
                  title: context.title || "",
                  affected_machine_part: context.affected_machine_part || "",
                  description: context.description || "",
                  alarm_code: context.alarm_code || "",
                  severity: context.severity || "",
                },
                display: {
                  title: schema.display.title,
                  summary: context.title || context.alarm_code
                    ? `${context.alarm_code || ""} — ${context.machine_name || machineContext?.model || ""}`.trim()
                    : undefined,
                  icon: schema.display.icon,
                },
                approval_tier: schema.approval_tier,
              },
            })
          );
          return;
        }
      }

      // Fallback: Unknown action ID — send to bot (old behavior)
      dispatch(markActionClicked({ messageId, actionId: action.id }));
      dispatch(
        sendMessage({
          message: "",
          machineId: machineContext?.machineId,
          threadId: activeThreadId || undefined,
          actionId: action.id,
          actionContext: action.context,
          originThreadId: activeThreadId || undefined,
        })
      );
    },
    [dispatch, messageId, machineContext, activeThreadId, isActedOn, isLoading]
  );

  // If an action request is already attached, don't render the buttons
  // (MidasActionCard will be rendered by ChatBotMessage instead)
  if (hasActionRequest) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        mt: "10px",
      }}
      role="group"
      aria-label={translate("midas.actions.actionGroup")}
    >
      {actions.map((action) => {
        const isClicked = clickedActionId === action.id;
        const isDimmed = isActedOn && !isClicked;

        return (
          <Button
            key={action.id}
            onClick={() => handleClick(action)}
            disabled={isActedOn || isLoading}
            size="small"
            variant={
              action.style === "primary"
                ? "contained"
                : action.style === "secondary"
                ? "outlined"
                : "text"
            }
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              padding: "6px 16px",
              minHeight: "34px",
              lineHeight: 1.4,
              transition: "all 0.2s ease",
              opacity: isDimmed ? 0.45 : 1,

              // Primary style — gold background
              ...(action.style === "primary" && {
                backgroundColor: midas.gold,
                color: "#FFFFFF",
                boxShadow: "0 2px 6px rgba(212, 160, 23, 0.25)",
                "&:hover": {
                  backgroundColor: midas.goldDark,
                  boxShadow: "0 3px 10px rgba(212, 160, 23, 0.35)",
                },
                "&.Mui-disabled": {
                  backgroundColor: isClicked ? midas.gold : midas.goldSoft,
                  color: isClicked ? "#FFFFFF" : dt.textMuted,
                  boxShadow: "none",
                  opacity: isDimmed ? 0.45 : 0.85,
                },
              }),

              // Secondary style — outlined gold
              ...(action.style === "secondary" && {
                borderColor: midas.gold,
                color: midas.gold,
                "&:hover": {
                  backgroundColor: midas.goldSoft,
                  borderColor: midas.goldDark,
                },
                "&.Mui-disabled": {
                  borderColor: isClicked ? midas.gold : dt.cardBorder,
                  color: isClicked ? midas.gold : dt.textMuted,
                  opacity: isDimmed ? 0.45 : 0.85,
                },
              }),

              // Text style — muted text
              ...(action.style === "text" && {
                color: dt.textSecondary,
                "&:hover": {
                  backgroundColor: midas.goldSoft,
                  color: midas.gold,
                },
                "&.Mui-disabled": {
                  color: isClicked ? dt.textSecondary : dt.textMuted,
                  opacity: isDimmed ? 0.45 : 0.85,
                },
              }),
            }}
          >
            {action.label}
            {isClicked && (
              <Box
                component="span"
                sx={{ ml: "4px", fontSize: "11px" }}
                aria-label={translate("midas.actions.actionTaken")}
              >
                &#10003;
              </Box>
            )}
          </Button>
        );
      })}
    </Box>
  );
};

export default ChatActionButtons;
